import { Fragment, useState } from 'react';
import { useQuery } from '@apollo/client';
import { endOfDay, startOfDay } from 'date-fns';
import { CSVLink, Flex, StyledDatePicker, StyledFormSelect, WrappedQuery } from '../';
import { ALL_INTERVENTIONS, ALL_SERVICE_PROVIDERS, EXPORT_INTERVENTIONS } from '../../../graphql';
import { formatSelectValue, formatInterventionsProfileData, getPortal, sanitizeData } from '../../../utils';
import { interventionHeaders } from '../../../consts';
import { getAllProgrammesQuery } from '../../../containers/shared/programmes/utils';

type Props = {
  programme: string;
  serviceProvider: string;
  intervention: string;
};

type State = {
  programmeIds?: string[];
  serviceProviderIds?: string[];
  interventionIds?: string[];
};

const InterventionExport = ({ programme, serviceProvider, intervention }: Props) => {
  const [portal] = useState(getPortal());
  const [state, setState] = useState<State>({});
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const { data: programmeData, error: programmeError, loading: programmeDataLoading } = useQuery(
    getAllProgrammesQuery(portal),
    {
      variables: {
        input: {
          isArchived: false,
          includeWhereReporter: true,
        },
      },
    }
  );
  const { data: spData, error: spError, loading: spDataLoading } = useQuery(ALL_SERVICE_PROVIDERS, {
    variables: {
      input: {
        ...(state.programmeIds && { programmeIds: state.programmeIds }),
      },
    },
  });
  const { data: interventionData, error: interventionError, loading: interventionDataLoading } = useQuery(
    ALL_INTERVENTIONS,
    {
      variables: {
        input: {
          ...(state.programmeIds && { programmeIds: state.programmeIds }),
          ...(state.serviceProviderIds && { serviceProviderIds: state.serviceProviderIds }),
        },
      },
    }
  );

  return (
    <Flex size="1" direction="column">
      <Fragment>
        <Flex size="1" direction="row" marginRight="10px">
          <StyledFormSelect
            isMulti={true}
            isClearable={true}
            name="programmeIds"
            label="Programme"
            placeholder={programmeDataLoading ? 'Data loading, Please wait' : 'Data ready, Please select option'}
            isDisabled={programmeDataLoading}
            onChange={(option: any[]) => {
              const newState = Object.assign({}, state, {
                [programme]: option ? option.map(opt => opt.value) : undefined,
              });
              setState(newState);
            }}
            options={
              programmeDataLoading || programmeError || !(programmeData && programmeData.programmes)
                ? []
                : programmeData.programmes.map((item: any) =>
                    formatSelectValue({
                      label: item.name,
                      value: item.id,
                    })
                  )
            }
          />
        </Flex>
        <Flex size="1" direction="row" marginRight="10px">
          <StyledFormSelect
            isMulti={true}
            isClearable={true}
            name="serviceProviderIds"
            label="Service Provider"
            placeholder={spDataLoading ? 'Data loading, Please wait' : 'Data ready, Please select option'}
            isDisabled={spDataLoading}
            onChange={(option: any[]) => {
              const newState = Object.assign({}, state, {
                [serviceProvider]: option ? option.map(opt => opt.value) : undefined,
              });
              setState(newState);
            }}
            options={
              spDataLoading || spError || !(spData && spData.serviceProviders)
                ? []
                : spData.serviceProviders.map((item: any) =>
                    formatSelectValue({
                      label: item.name,
                      value: item.id,
                    })
                  )
            }
          />
        </Flex>
        <Flex size="1" direction="row" marginRight="10px">
          <StyledFormSelect
            isMulti={true}
            isClearable={true}
            name="interventionIds"
            label="Intervention"
            placeholder={interventionDataLoading ? 'Data loading, Please wait' : 'Data ready, Please select option'}
            isDisabled={interventionDataLoading}
            onChange={(option: any[]) => {
              const newState = Object.assign({}, state, {
                [intervention]: option ? option.map(opt => opt.value) : undefined,
              });
              setState(newState);
            }}
            options={
              interventionDataLoading || interventionError || !(interventionData && interventionData.allInterventions)
                ? []
                : interventionData.allInterventions.map((item: any) =>
                    formatSelectValue({
                      label: item.name,
                      value: item.id,
                    })
                  )
            }
          />
        </Flex>
      </Fragment>
      <Flex size="1.2" direction="column">
        <StyledDatePicker
          label="Intervention Date"
          name={`interventionDate`}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
        />
      </Flex>

      <WrappedQuery
        query={EXPORT_INTERVENTIONS}
        variables={{
          input: {
            first: 10,
            ...(state.interventionIds && { interventionIds: state.interventionIds }),
            ...(state.serviceProviderIds && { serviceProviderIds: state.serviceProviderIds }),
            ...(state.programmeIds && { programmeIds: state.programmeIds }),
            ...(startDate && { startDate: startOfDay(new Date(startDate)) }),
            ...(endDate && { endDate: endOfDay(new Date(endDate)) }),
          },
        }}
        overrideStates={true}
      >
        {({ loading, error, data, fetchMore }) => {
          if (loading) {
            return <div>Preparing data for export...</div>;
          }

          if (error) {
            return <div>Something went wrong</div>;
          }

          // call the next page of the exportSchools query
          data.interventions &&
            data.interventions.pageInfo &&
            data.interventions.pageInfo.hasNextPage &&
            fetchMore({
              variables: {
                input: {
                  first: 10,
                  ...(state.interventionIds && { interventionIds: state.interventionIds }),
                  ...(state.serviceProviderIds && { serviceProviderIds: state.serviceProviderIds }),
                  ...(state.programmeIds && { programmeIds: state.programmeIds }),
                  ...(startDate && { startDate: startOfDay(new Date(startDate)) }),
                  ...(endDate && { endDate: endOfDay(new Date(endDate)) }),
                  after: data.interventions && data.interventions.pageInfo && data.interventions.pageInfo.endCursor,
                },
              },
              updateQuery: (previousResult, { fetchMoreResult }) => {
                const newInterventions = fetchMoreResult.interventions.edges;
                const previousInterventions = previousResult.interventions ? previousResult.interventions.edges : [];
                return {
                  interventions: {
                    aggregate: fetchMoreResult.interventions.aggregate,
                    pageInfo: fetchMoreResult.interventions.pageInfo,
                    edges: [...previousInterventions, ...newInterventions],
                    __typename: fetchMoreResult.interventions.__typename,
                  },
                };
              },
            });

          if (data.interventions && data.interventions.pageInfo && data.interventions.pageInfo.hasNextPage) {
            return <div>Fetching more pages...</div>;
          }

          const formatted: any[] =
            data.interventions && data.interventions.edges
              ? formatInterventionsProfileData({ data: data.interventions.edges })
              : [];

          const sanitized = formatted.length ? sanitizeData(formatted) : [];
          return (
            <Flex size="1" direction="row" marginRight="10px">
              {sanitized && sanitized.length > 0 && (
                <CSVLink
                  filename={`InterventionsSPExport-${new Date()}.csv`}
                  headers={interventionHeaders}
                  data={sanitized}
                >
                  Export CSV
                </CSVLink>
              )}

              {sanitized && sanitized.length < 1 && <span>No interventions data for given parameters</span>}
            </Flex>
          );
        }}
      </WrappedQuery>
    </Flex>
  );
};

export default InterventionExport;
